import "./dropDown.css";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { updateDropdownValue } from "../../redux/reducers/paginationReducer";
import InfoCircle from "../../assets/info-circle.svg";
import CrossIcon from "../../assets/cross.svg";

interface IDropDownProps {
  options?: any;
  onChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  value?: any;
  wrapperClass?: string;
  selectClassName?: string;
  placeHolder?: string;
  name?: string;
  heading?: string;
  inputError?: boolean;
  disabled?: boolean;
  /**
   * Native <select> option popups can overflow small viewports (especially inside modals).
   * Set false to render a constrained in-DOM options panel.
   */
  useNativeSelect?: boolean;
  /** Shown above the custom list when `useNativeSelect` is false; omit for compact lists (e.g. pagination). */
  dropdownListCaption?: React.ReactNode;
  /** Filter options by label/fullLabel in the custom panel (implies `useNativeSelect={false}`). */
  searchable?: boolean;
  searchPlaceholder?: string;
  /** When set, search is handled externally (e.g. API); options are not filtered client-side. */
  onSearchChange?: (query: string) => void;
  /** Debounce delay (ms) before `onSearchChange` runs while typing. */
  searchDebounceMs?: number;
  isSearchLoading?: boolean;
  /** Show a clear control when a value is selected (searchable custom panel). */
  clearable?: boolean;
}

const CustomDropDown = (props: IDropDownProps) => {
  const {
    options,
    onChange,
    value,
    wrapperClass,
    selectClassName,
    placeHolder,
    name,
    heading,
    inputError,
    disabled,
    useNativeSelect = true,
    dropdownListCaption,
    searchable = false,
    searchPlaceholder = "Search...",
    onSearchChange,
    searchDebounceMs = 400,
    isSearchLoading = false,
    clearable = false,
  } = props;
  const dispatch = useDispatch();
  const selectId = useId();

  const dropdownRef: any = useRef(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const cancelDebouncedSearch = () => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }
  };

  const triggerSearchChange = (query: string, immediate = false) => {
    if (!onSearchChange) return;
    cancelDebouncedSearch();
    if (immediate) {
      onSearchChange(query);
      return;
    }
    searchDebounceRef.current = setTimeout(() => {
      onSearchChange(query);
      searchDebounceRef.current = null;
    }, searchDebounceMs);
  };

  const useCustomPanel = searchable || !useNativeSelect;

  const addClass = () => {
    dropdownRef.current?.children?.[1]?.classList?.add("setLabel");
  };

  let label =
    placeHolder?.replace(/\b\w/g, (match) => match.toUpperCase()) || "";

  const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newValue = event.target.value;
    dispatch(updateDropdownValue(newValue)); // Dispatch action to update Redux state
  };

  const handleCombinedChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    handleChange(event);
    onChange && onChange(event); // Call the external onChange handler if provided
  };

  // Get the full label for the selected option to show in tooltip
  const selectedOption = options?.find(
    (opt: any) => String(opt?.value) === String(value),
  );
  const selectedFullLabel =
    selectedOption?.fullLabel || selectedOption?.label || "";

  const selectedDisplayLabel = useMemo(() => {
    if (value === undefined || value === null || value === "") return "";
    return selectedOption?.label || "";
  }, [selectedOption, value]);

  const displayOptions = useMemo(() => {
    if (!searchable) return options || [];
    if (onSearchChange) return options || [];
    if (!searchQuery.trim()) return options || [];
    const query = searchQuery.trim().toLowerCase();
    return (options || []).filter((option: any) => {
      const searchableText = [option?.label, option?.fullLabel, option?.value]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return searchableText.includes(query);
    });
  }, [options, searchable, searchQuery, onSearchChange]);

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    triggerSearchChange(query);
  };

  const hasValue =
    value !== undefined && value !== null && String(value) !== "";

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setSearchQuery("");
    triggerSearchChange("", true);
    setIsOpen(false);
    dispatch(updateDropdownValue(""));
    onChange?.({ target: { value: "" } } as React.ChangeEvent<HTMLSelectElement>);
  };

  const showClearButton =
    clearable && hasValue && !disabled && !(searchable && isOpen);

  useEffect(() => {
    if (!isOpen) {
      setSearchQuery("");
      if (onSearchChange) {
        triggerSearchChange("", true);
      }
      return;
    }
    if (searchable) {
      if (onSearchChange) {
        triggerSearchChange("", true);
      }
      const timer = setTimeout(() => searchInputRef.current?.focus(), 0);
      return () => clearTimeout(timer);
    }
  }, [isOpen, searchable]);

  useEffect(() => {
    return () => cancelDebouncedSearch();
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const closeIfOutside = (e: Event) => {
      const target = e.target as Node;
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(target) &&
        menuRef.current &&
        !menuRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", closeIfOutside);
    document.addEventListener("touchstart", closeIfOutside, { passive: true });
    return () => {
      document.removeEventListener("mousedown", closeIfOutside);
      document.removeEventListener("touchstart", closeIfOutside);
    };
  }, [isOpen]);

  return (
    <>
      <div className={`${wrapperClass} select-container`} ref={dropdownRef}>
        <div className="relative w-full">
        {!useCustomPanel ? (
          <select
            id={selectId}
            onChange={handleCombinedChange}
            value={value || ""}
            placeholder={placeHolder}
            name={name}
            disabled={disabled}
            title={selectedFullLabel} // Show full address on hover for selected option
            className={`${selectClassName} select-dropdown rounded cursor-pointer ${
              inputError && !value
                ? "!border-red-500"
                : "border-[1px] border-[#A4A4A4]"
            } p-[10px] gap-[10px] h-[48px] font-Open text-[12px] text-[#1C1C1C] outline-none`}
            onClick={addClass}
          >
            {heading && (
              <option
                value=""
                className="text-[12px] text-[#777777] absolute leading-4 font-Open"
                disabled
                hidden
              >
               {heading}
              </option>
            )}
            {options?.map((option: any, index: number) => {
              const fullLabel = option?.fullLabel || option?.label || "";
              return (
                <option
                  key={index}
                  value={option.value}
                  className="text-[12px] text-[#1C1C1C] leading-4 font-Open"
                  disabled={option?.disabled || false}
                  title={fullLabel} // Show full address on hover for each option
                >
                  {option.label}
                </option>
              );
            })}
          </select>
        ) : searchable && isOpen ? (
          <input
            ref={searchInputRef}
            type="text"
            id={selectId}
            name={name ? `${name}-search` : undefined}
            disabled={disabled}
            value={searchQuery}
            placeholder={searchPlaceholder}
            onChange={handleSearchInputChange}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === "Escape") {
                setIsOpen(false);
              }
            }}
            autoComplete="off"
            className={`${selectClassName} select-dropdown searchable-trigger rounded cursor-text ${
              inputError && !value
                ? "!border-red-500"
                : "border-[1px] border-[#A4A4A4]"
            } p-[10px] pr-8 gap-[10px] font-Open text-[12px] text-[#1C1C1C] outline-none w-full bg-white`}
          />
        ) : (
          <button
            type="button"
            id={selectId}
            disabled={disabled}
            title={selectedFullLabel}
            onClick={() => setIsOpen((v) => !v)}
            className={`${selectClassName} select-dropdown rounded cursor-pointer ${
              inputError && !value
                ? "!border-red-500"
                : "border-[1px] border-[#A4A4A4]"
            } p-[10px] gap-[10px] h-[48px] font-Open text-[12px] text-left text-[#1C1C1C] outline-none w-full bg-white ${
              showClearButton ? "!pr-12" : ""
            }`}
          >
            <span
              className={`block truncate ${
                !selectedDisplayLabel ? "text-[#777777]" : ""
              }`}
            >
              {selectedDisplayLabel ||
                (searchable ? searchPlaceholder : heading) ||
                ""}
            </span>
          </button>
        )}

        {showClearButton && (
          <button
            type="button"
            aria-label="Clear selection"
            onClick={handleClear}
            className="absolute right-7 top-1/2 z-10 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-full hover:bg-[#F0F0F0]"
          >
            <img src={CrossIcon} alt="" className="h-3 w-3" />
          </button>
        )}
        </div>

        {useCustomPanel && isOpen && (
          <div
            ref={menuRef}
            className="absolute z-[9999] top-[calc(100%+4px)] left-0 right-0 bg-white border border-[#E8E8E8] rounded-md shadow-lg w-full max-h-[35vh] flex flex-col overflow-hidden"
          >
            {dropdownListCaption != null && (
              <div className="shrink-0 px-3 py-2 text-[12px] font-Open text-[#777777] border-b border-[#F0F0F0]">
                {dropdownListCaption}
              </div>
            )}
            <div className="overflow-auto flex-1 min-h-0">
              {isSearchLoading ? (
                <p className="px-3 py-3 text-[12px] font-Open text-[#777777]">
                  Searching...
                </p>
              ) : displayOptions.length === 0 ? (
                <p className="px-3 py-3 text-[12px] font-Open text-[#777777]">
                  No results found
                </p>
              ) : (
                displayOptions.map((option: any, index: number) => {
                  const isDisabled = option?.disabled || false;
                  const isSelected = option?.value === value;
                  return (
                    <button
                      key={index}
                      type="button"
                      disabled={isDisabled}
                      title={option?.fullLabel || option?.label || ""}
                      className={`w-full text-left px-3 py-2 text-[12px] font-Open ${
                        isSelected ? "bg-[#E5EDFF]" : "bg-white"
                      } ${
                        isDisabled
                          ? "opacity-50 cursor-not-allowed"
                          : "hover:bg-[#F6F6F6]"
                      } `}
                      onClick={() => {
                        dispatch(updateDropdownValue(option.value));
                        // Normalize value to string to match <select> event semantics
                        onChange?.({
                          target: { value: String(option.value) },
                        } as any);
                        setIsOpen(false);
                      }}
                    >
                      <span className="block truncate">{option.label}</span>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>
      {/* {inputError && !value && (
        <span
          className={`text-[red] transition-all ease-out h-0  h-[18px]  delay-100 font-Open text-[11px] mt-1 px-2 `}
        >
          Field is required
        </span>
      )} */}
      {inputError && !value && (
        <div className="flex items-center gap-x-1 mt-1">
          <img src={InfoCircle} alt="" width={16} height={16} />
          <span className="font-normal text-[#F35838] text-xs leading-3 transition-all ease-out delay-100">
            Field is required
          </span>
        </div>
      )}
    </>
  );
};

export default CustomDropDown;
